import type { CommandMember, CommandTask } from '../../command/types.ts';

export type ConstructionTask = Pick<CommandTask, 'id' | 'title' | 'status' | 'scope' | 'ownerId' | 'createdBy' | 'createdAt'>;
export type ConstructionMember = Pick<CommandMember, 'id' | 'name'>;
export type Position = [number, number, number];
export type SiteBlock = { position: Position; size: Position; color: string };
export type TaskBlock = { task: ConstructionTask; position: Position; ordinal: number };
export const TASK_BLOCK_SIZE: Position = [2.08, 0.88, 2.08];
export const TASK_LAYER_HEIGHT = 1.02;

export function constructionSeed(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) hash = Math.imul(hash ^ value.charCodeAt(i), 16777619);
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x7feb352d);
  hash ^= hash >>> 15;
  return hash >>> 0;
}

function hueToHex(hue: number, saturation: number, lightness: number): string {
  const amplitude = saturation * Math.min(lightness, 1 - lightness);
  return `#${[0, 8, 4].map(offset => {
    const k = (offset + hue / 30) % 12;
    const value = lightness - amplitude * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(255 * value).toString(16).padStart(2, '0');
  }).join('')}`;
}

// Guest IDs already contain randomness. Deriving the palette from those immutable
// IDs keeps it identical across browsers, reloads, presence changes and filters.
// Resolve exact RGB collisions against the complete room membership, never against
// only the currently visible tasks or online members.
export function participantColors(members: readonly ConstructionMember[]): Record<string, string> {
  const result: Record<string, string> = {};
  const used = new Set<string>();
  const ids = [...new Set(members.map(member => member.id))].sort();
  for (const id of ids) {
    const seed = constructionSeed(`atrium/participant/${id}`);
    const hue = (seed % 36000) / 100;
    const saturation = 0.57 + ((seed >>> 16) % 19) / 100;
    const lightness = 0.59 + ((seed >>> 24) % 10) / 100;
    let step = 0;
    let color = hueToHex(hue, saturation, lightness);
    while (used.has(color)) {
      step += 1;
      color = hueToHex((hue + step * 137.508) % 360, saturation, lightness);
    }
    result[id] = color;
    used.add(color);
  }
  return result;
}

export function taskParticipant(task: ConstructionTask): string {
  return task.ownerId || task.createdBy;
}

export function taskBlockLift(status: ConstructionTask['status']): number {
  // Keep every status inside the existing floor clearance. Raising an active
  // block above this gap would intersect tasks in the next occupied layer.
  const clearance = TASK_LAYER_HEIGHT - TASK_BLOCK_SIZE[1] - 0.04;
  return status === 'active' ? clearance : status === 'todo' ? clearance * 0.4 : 0;
}

export function constructionTasks(tasks: readonly ConstructionTask[]): TaskBlock[] {
  return [...tasks].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id)).map((task, index) => {
    const cell = index % 30;
    const row = Math.floor(cell / 6);
    const column = row % 2 ? 5 - cell % 6 : cell % 6;
    return {
      task,
      ordinal: index + 1,
      position: [(column - 2.5) * 2.24, 0.54 + Math.floor(index / 30) * TASK_LAYER_HEIGHT, 7.8 - row * 2.24],
    };
  });
}

// A bounded, instanced city extends past the fog and camera frustum. The neutral
// structure is scenery; only constructionTasks() creates participant/task blocks.
export function constructionSite(reduced = false) {
  const blocks: SiteBlock[] = [];
  const frames: SiteBlock[] = [];
  const deliveries: SiteBlock[] = [];
  const radius = reduced ? 7 : 9;
  const palette = ['#536064', '#697372', '#7c837d', '#899088', '#5e6d6c'];
  for (let row = reduced ? -10 : -14; row <= 5; row += 1) {
    for (let column = -radius; column <= radius; column += 1) {
      const x = column * 10.8;
      const z = row * 10.8;
      if (Math.abs(x) < 16 && z > -15 && z < 19) continue;
      const seed = constructionSeed(`atrium/site/${column}/${row}`);
      if (seed % 9 === 0) continue;
      const height = 2 + seed % 9;
      const color = palette[(seed >>> 8) % palette.length];
      blocks.push({ position: [x, -0.22, z], size: [5.5, 0.38, 5.5], color: '#303e41' });
      for (let level = 0; level < height; level += 1) {
        for (let cell = 0; cell < 4; cell += 1) {
          if (level > height - 3 && ((seed >>> cell) + level) % 3 === 0) continue;
          blocks.push({
            position: [x + (cell % 2 - 0.5) * 2.3, 0.48 + level * 1.03, z + (Math.floor(cell / 2) - 0.5) * 2.3],
            size: [2.18, 0.92, 2.18], color,
          });
        }
      }
      if (seed % 4 === 0) frames.push({ position: [x, height * 0.515 + 1, z], size: [4.8, height * 1.03 + 2, 4.8], color: '#728381' });
      if (deliveries.length < (reduced ? 10 : 20) && Math.abs(column) < 4 && row > -5 && row < 3 && seed % 3 === 0) {
        deliveries.push({ position: [x - 1.15, height * 1.03 + 0.48, z - 1.15], size: [2.18, 0.92, 2.18], color: '#a3ada3' });
      }
    }
  }
  return { blocks, frames, deliveries };
}
